<script setup>
// Astra DS · NavItem — celda de navegación de la cabecera.
//
// Figma: NavItem 668:657 (átomo).
//
// ── El estado se dice con color, y solo con color ──
//
// 🔴 **Aquí había una regla de 2 px al pie del activo** (`rule 668:655`). Se
// retira el 07.09.26: con el icono en acento serían dos marcas para el mismo
// estado. Es la decisión que la app tomó el 06.09 en su `TabBar`, así que las
// dos superficies dicen «estás dentro» igual.
//
//   reposo  texto `secondary` · icono `tertiary`
//   hover   texto `primary`   · icono `primary`
//   activo  texto `primary`   · icono `accent`
//
// El icono y la palabra **no van del mismo color en el mismo estado**, así que
// el icono no puede heredar del enlace: lleva el suyo.
//
// ⚠️ El hover **no rellena**. El fondo es el recurso con el que este sistema
// construye profundidad —`canvas` → `elevated` → `subtle` → `strong`— y el
// hover no es profundidad, es atención. Tampoco lleva acento: el acento
// significa «estás dentro», y si dijera también «estás encima» dejaría de
// decir las dos cosas. (DESIGN.md, entrega del 07.09.26.)
import AstraIcon from "@/components/icons/AstraIcon.vue";

defineProps({
  label: { type: String, required: true },
  // Nombre en `AstraIcon`. Sin él la celda es solo palabra, que es como vivió
  // hasta el 07.09.26.
  icono: { type: String, default: "" },
  to: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
});
</script>

<template>
  <component
    :is="to ? 'router-link' : 'button'"
    :to="to || undefined"
    :type="to ? null : 'button'"
    class="nav-item"
    :class="{ 'is-active': active }"
    :aria-current="active ? 'page' : null"
  >
    <AstraIcon v-if="icono" class="nav-item__icono" :name="icono" :size="15" />
    <span class="nav-item__label">{{ label }}</span>
  </component>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.nav-item {
  position: relative;
  display: flex;
  flex: none;
  // 8, que está en la escala. La referencia traía 9 y 17, que no lo están.
  gap: 8px;
  align-items: center;
  justify-content: center;
  // `align-self: stretch` y no `height: 100%`. El porcentaje resuelve contra la
  // caja de contenido del padre, así que cualquier relleno heredado lo encoge;
  // stretch estira contra la línea flex y no depende de nada de fuera.
  align-self: stretch;
  padding: 0 16px;
  border: 0;
  border-right: 1px solid var(--color-border-default);
  border-radius: 0;
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.12s linear;

  &:hover {
    color: var(--color-text-primary);

    .nav-item__icono {
      color: var(--color-icon-primary);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }

  &.is-active {
    color: var(--color-text-primary);

    .nav-item__icono {
      color: var(--color-icon-accent);
    }
  }
}

.nav-item__icono {
  color: var(--color-icon-tertiary);
  transition: color 0.12s linear;
}

.nav-item__label {
  @include t.mono-label;
}
</style>
